import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BrandMark } from "./BrandMark";
import { homeForRole, postAuthRedirect } from "../lib/roleHome";

const ROLE_RANK = { customer: 1, staff: 2, manager: 3, admin: 4 };

const FullPageLoader = () => (
  <main
    data-testid="auth-loading"
    className="min-h-screen bg-napoli-cream flex flex-col items-center justify-center"
  >
    <BrandMark size="md" theme="light" />
    <p className="mt-8 font-body italic text-napoli-espresso-soft text-sm">
      One moment…
    </p>
  </main>
);

/** Wrap routes that require any authenticated user (any role). */
export const ProtectedRoute = ({ children, role = "customer" }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  const rank = ROLE_RANK[user.role] || 0;
  const min = ROLE_RANK[role] || 0;
  if (rank < min) {
    // Redirect them to their own role-appropriate home, not always /customer.
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
};

/** Wrap routes that should only be visible to logged-out users (login, signup). */
export const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) {
    return <Navigate to={postAuthRedirect(user)} replace />;
  }
  return children;
};

/** Inside an authenticated area, force profile completion before letting
 *  the user reach feature pages. */
export const RequireProfile = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.profile_completed) {
    return <Navigate to="/complete-profile" replace />;
  }
  return children;
};

export default ProtectedRoute;
